import { useEffect, useRef, useState } from "react";

const copy = {
  en: {
    eyebrow: "Stay in the loop", title: "The latest from MNEZY.",
    body: "New features, behind-the-scenes moments, and a little more colour.",
    links: "Follow MNEZY", previous: "Previous posts", next: "Next posts",
    open: "View post", empty: "Catch the latest updates on your favourite platform.",
    all: "All platforms",
  },
  fr: {
    eyebrow: "Gardez le fil", title: "Les nouvelles de MNEZY.",
    body: "Nouveautés, coulisses et un peu plus de couleur.",
    links: "Suivre MNEZY", previous: "Publications précédentes", next: "Publications suivantes",
    open: "Voir la publication", empty: "Retrouvez les dernières nouvelles sur votre plateforme préférée.",
    all: "Toutes les plateformes",
  },
};
const platforms = ["Instagram", "Threads", "X"];
const hosts = {
  Instagram: ["instagram.com", "www.instagram.com"],
  Threads: ["threads.net", "www.threads.net", "threads.com", "www.threads.com"],
  X: ["x.com", "www.x.com", "twitter.com", "www.twitter.com"],
};
const feedUrl = process.env.REACT_APP_SOCIAL_FEED_URL || "https://raw.githubusercontent.com/marcgeeklaberge/MNEZY/social-feed/social-posts.json";

function safeUrl(value, allowedHosts) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password && (!allowedHosts || allowedHosts.includes(url.hostname)) ? url.href : null;
  } catch { return null; }
}

export function normalizePosts(data) {
  const seen = new Set();
  return (Array.isArray(data?.posts) ? data.posts : []).filter((post) => {
    if (!post || !hosts[post.platform] || typeof post.id !== "string" || !safeUrl(post.url, hosts[post.platform]) || !Number.isFinite(Date.parse(post.date))) return false;
    const key = `${post.platform}:${post.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((post) => ({ ...post, text: typeof post.text === "string" ? post.text : "", image: safeUrl(post.image) }))
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date)).slice(0, 24);
}

export default function SocialUpdates({ locale, links, initialPosts }) {
  const [posts, setPosts] = useState(() => normalizePosts({ posts: initialPosts }));
  const [platform, setPlatform] = useState("all");
  const rail = useRef(null);
  const c = copy[locale] || copy.en;
  useEffect(() => {
    if (initialPosts) return undefined;
    const controller = new AbortController();
    fetch(feedUrl, { signal: controller.signal, cache: "no-cache" })
      .then((response) => { if (!response.ok) throw new Error("Feed unavailable"); return response.json(); })
      .catch((error) => {
        if (controller.signal.aborted) throw error;
        return fetch(`${process.env.PUBLIC_URL || ""}/social-posts.json`, { signal: controller.signal })
          .then((response) => { if (!response.ok) throw new Error("Feed unavailable"); return response.json(); });
      })
      .then((data) => setPosts(normalizePosts(data)))
      .catch(() => { /* Profile links remain available if the feed is not connected or is offline. */ });
    return () => controller.abort();
  }, [initialPosts]);
  const visible = posts.filter((post) => platform === "all" || post.platform === platform);
  const move = (direction) => rail.current?.scrollBy({ left: direction * rail.current.clientWidth * .85, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  return (
    <section className="social-updates section-wrap" aria-labelledby="social-updates-title">
      <div className="section-heading">
        <div><p className="eyebrow">{c.eyebrow}</p><h2 id="social-updates-title">{c.title}</h2></div>
        <p>{c.body}</p>
      </div>
      {posts.length > 0 ? <>
        <div className="social-updates__toolbar">
          <div className="social-updates__filters" role="group" aria-label={c.all}>
            {["all", ...platforms.filter((name) => posts.some((post) => post.platform === name))].map((name) => (
              <button key={name} type="button" aria-pressed={platform === name} onClick={() => { setPlatform(name); if (rail.current) rail.current.scrollLeft = 0; }}>{name === "all" ? c.all : name}</button>
            ))}
          </div>
          <div className="social-updates__arrows">
            <button type="button" aria-label={c.previous} onClick={() => move(-1)}>←</button>
            <button type="button" aria-label={c.next} onClick={() => move(1)}>→</button>
          </div>
        </div>
        <div className="social-updates__rail" ref={rail} tabIndex={0} role="region" aria-label={c.title}>
          {visible.map((post) => <article className={`social-post social-post--${post.platform.toLowerCase()}`} key={`${post.platform}:${post.id}`}>
            <div className="social-post__meta"><span>{post.platform}</span><time dateTime={post.date}>{new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric", year: "numeric", timeZone: "America/Toronto" }).format(new Date(post.date))}</time></div>
            {post.image && <a className="social-post__image" href={post.url} target="_blank" rel="noreferrer" aria-label={`${c.open} — ${post.platform}`}><img src={post.image} alt="" loading="lazy" onError={(event) => { event.currentTarget.style.display = "none"; }} /></a>}
            <div className="social-post__body"><span className="social-post__author">@mnezyapp</span><p>{post.text}</p><a href={post.url} target="_blank" rel="noreferrer">{c.open} <span aria-hidden="true">↗</span></a></div>
          </article>)}
        </div>
      </> : <p className="social-updates__empty">{c.empty}</p>}
      <nav className="social-links social-updates__links" aria-label={c.links}>
        {links.map((link) => <a key={link.href} href={link.href} target="_blank" rel="noreferrer" aria-label={`MNEZY on ${link.label}`}>{link.label} ↗</a>)}
      </nav>
    </section>
  );
}
